import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "@/lib/format";
import { BOOKING_STATUS, SOURCES, PAYMENT_METHODS } from "@/lib/bookingConstants";
import { COMMISSION_STATUS, commissionRuleText } from "@/lib/partnerConstants";
import { PaymentBadge, BookingStatusBadge } from "@/components/dashboard/StatusBadge";
import EncaisserDialog from "@/components/reservations/EncaisserDialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Loader2, Wallet, LogIn, Play, CheckCircle2, XCircle, UserX, Pencil, AlertTriangle,
} from "lucide-react";

const NEXT_STATUS = { CONFIRMED: ["CHECKED_IN", "Client arrivé", LogIn], CHECKED_IN: ["IN_PROGRESS", "Démarrer l'activité", Play], IN_PROGRESS: ["COMPLETED", "Terminer l'activité", CheckCircle2] };

export default function BookingDetailsDrawer({ bookingId, open, onOpenChange, onEdit }) {
  const { orgId, can } = useAuth();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);

  const { data: b, isLoading } = useQuery({
    queryKey: ["booking", bookingId, orgId],
    queryFn: async () => (await api.get(`/api/bookings/${bookingId}`, { params: { organization_id: orgId } })).data,
    enabled: open && !!bookingId,
  });

  const act = useMutation({
    mutationFn: async ({ url, body }) => (await api.post(url, body || {}, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Réservation mise à jour"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  const doStatus = (s) => act.mutate({ url: `/api/bookings/${bookingId}/status`, body: { status: s } });
  const doCancel = () => { const r = window.prompt("Raison de l'annulation (optionnel) :", ""); if (r !== null) act.mutate({ url: `/api/bookings/${bookingId}/cancel`, body: { cancellation_reason: r } }); };
  const doNoShow = () => act.mutate({ url: `/api/bookings/${bookingId}/no-show` });

  const next = b && NEXT_STATUS[b.status];
  const closed = b && ["CANCELLED", "NO_SHOW", "COMPLETED"].includes(b.status);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" aria-describedby={undefined} className="w-full overflow-y-auto p-0 sm:max-w-lg" data-testid="booking-details-drawer">
        {isLoading || !b ? (
          <div className="flex h-full items-center justify-center">
            <SheetTitle className="sr-only">Chargement de la réservation</SheetTitle>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <SheetHeader className="border-b border-slate-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <SheetTitle className="font-mono">{b.booking_reference}</SheetTitle>
                <div className="flex gap-2"><PaymentBadge status={b.payment_status} /><BookingStatusBadge status={b.status} /></div>
              </div>
              <SheetDescription className="sr-only">Détails et historique de la réservation {b.booking_reference}.</SheetDescription>
            </SheetHeader>

            <div className="space-y-5 p-6">
              {b.status === "COMPLETED" && b.payment_status === "UNPAID" && (
                <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700" data-testid="unpaid-completed-warning">
                  <AlertTriangle className="h-4 w-4" /> Activité terminée — paiement non encaissé
                </div>
              )}

              <Block title="Client">
                <p className="text-sm font-semibold text-slate-900">{b.customer_name}</p>
                <p className="text-sm text-slate-500">{b.customer?.phone}{b.customer?.email ? ` · ${b.customer.email}` : ""}</p>
              </Block>

              <div className="grid grid-cols-2 gap-4">
                <Block title="Activité"><p className="text-sm font-semibold text-slate-900">{b.activity_name}</p><p className="text-sm text-slate-500">{b.participants_count} participants</p></Block>
                <Block title="Horaire"><p className="text-sm font-semibold text-slate-900">{b.booking_date}</p><p className="text-sm text-slate-500">{b.start_time} → {b.end_time}</p></Block>
              </div>

              <Block title="Ressources">
                <p className="text-sm text-slate-700">{b.vehicles?.length ? b.vehicles.map((v) => v.code).join(", ") : "Aucun véhicule"}</p>
                <p className="text-sm text-slate-500">{b.employees?.length ? b.employees.map((e) => e.name).join(", ") : "Aucun guide"}</p>
              </Block>

              <Block title="Tarification">
                <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm" data-testid="booking-breakdown">
                  {(b.booking_items || []).map((i, idx) => (
                    <div key={idx} className="flex justify-between"><span className="text-slate-600">{i.name_snapshot} {i.quantity > 1 ? <span className="text-slate-400">{i.quantity} × {formatMoney(i.unit_price, b.currency)}</span> : ""}</span><span className="font-mono text-slate-800">{formatMoney(i.line_total, b.currency)}</span></div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-1"><span className="text-slate-500">Sous-total</span><span className="font-mono text-slate-900">{formatMoney(b.subtotal_cents ?? b.total_amount_cents, b.currency)}</span></div>
                  {b.discount_amount_cents > 0 && <div className="flex justify-between text-emerald-700"><span>Réduction{b.discount_reason ? ` · ${b.discount_reason}` : ""}</span><span className="font-mono">-{formatMoney(b.discount_amount_cents, b.currency)}</span></div>}
                  <div className="flex justify-between border-t border-slate-200 pt-1"><span className="font-semibold text-slate-900">Total</span><span className="font-mono text-lg font-bold text-slate-900">{formatMoney(b.total_amount_cents, b.currency)}</span></div>
                </div>
                {b.payment_status === "PAID" && b.payment ? (
                  <div className="mt-1 rounded-lg bg-emerald-50 p-2.5 text-xs text-emerald-800" data-testid="payment-recorded-info">
                    ✓ Payé · {PAYMENT_METHODS[b.payment.payment_method]} · {b.payment.paid_date} · par {b.payment.recorded_by_name}
                  </div>
                ) : can("payments.record") && !["CANCELLED", "NO_SHOW"].includes(b.status) && (
                  <button onClick={() => setPayOpen(true)} data-testid="encaisser-button" className="mt-2 flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                    <Wallet className="h-4 w-4" /> Encaisser — {formatMoney(b.total_amount_cents, b.currency)}
                  </button>
                )}
                {b.price_override_cents != null && <p className="mt-1 text-xs text-amber-600">Prix forcé : {formatMoney(b.price_override_cents, b.currency)}{b.price_override_reason ? ` · ${b.price_override_reason}` : ""} (calculé {formatMoney(b.calculated_total_cents, b.currency)})</p>}
              </Block>

              {b.partner_id && (
                <Block title="Partenaire & commission">
                  <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm" data-testid="booking-commission">
                    <div className="flex justify-between"><span className="font-medium text-slate-800">{b.partner_name_snapshot}</span><span className="text-slate-500">{commissionRuleText(b.commission_type_snapshot, b.commission_value_snapshot, b.currency)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Base de calcul</span><span className="font-mono text-slate-700">{formatMoney(b.commission_base_amount, b.currency)}</span></div>
                    <div className="flex justify-between border-t border-slate-200 pt-1"><span className="font-semibold text-slate-900">Commission</span><span className="font-mono font-bold text-indigo-700" data-testid="commission-amount-detail">{formatMoney(b.commission_amount, b.currency)}</span></div>
                    <div className="flex items-center justify-between pt-1"><span className="text-slate-500">Statut</span>{(() => { const cs = COMMISSION_STATUS[b.commission_status]; return cs ? <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${cs.cls}`} data-testid="commission-status-badge">{cs.label}</span> : <span className="text-xs text-slate-400">—</span>; })()}</div>
                    {b.commission_override && <p className="text-xs text-amber-600" data-testid="commission-override-note">Commission ajustée{b.commission_override_reason ? ` · ${b.commission_override_reason}` : ""}</p>}
                  </div>
                </Block>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Block title="Source"><p className="text-sm text-slate-700">{SOURCES[b.source] || b.source}</p></Block>
                {b.internal_notes && <Block title="Notes"><p className="text-sm text-slate-700">{b.internal_notes}</p></Block>}
              </div>

              {/* Contextual actions */}
              {!closed && (
                <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                  {next && can("bookings.update_status") && (
                    <button onClick={() => doStatus(next[0])} data-testid="advance-status-button" className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                      {React.createElement(next[2], { className: "h-4 w-4" })} {next[1]}
                    </button>
                  )}
                  {can("bookings.manage") && (
                    <>
                      <button onClick={() => onEdit(b)} data-testid="edit-booking-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><Pencil className="h-4 w-4" /> Modifier</button>
                      <button onClick={doCancel} data-testid="cancel-booking-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50"><XCircle className="h-4 w-4" /> Annuler</button>
                      <button onClick={doNoShow} data-testid="no-show-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"><UserX className="h-4 w-4" /> Absent</button>
                    </>
                  )}
                </div>
              )}

              <BookingHistory bookingId={b.id} currency={b.currency} />
              <p className="text-xs text-slate-400">Créée le {b.created_at?.slice(0, 10)} · Dernière modif. {b.updated_at?.slice(0, 10)}</p>
            </div>
            <EncaisserDialog booking={b} open={payOpen} onOpenChange={setPayOpen} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Block = ({ title, children }) => (
  <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p><div className="mt-1">{children}</div></div>
);

const ACTION_LABELS = {
  "booking.created": "Réservation créée", "booking.updated": "Réservation modifiée",
  "booking.repriced": "Tarification recalculée", "booking.price_override": "Prix forcé",
  "booking.status_changed": "Statut modifié", "booking.cancelled": "Réservation annulée",
  "booking.no_show": "Client absent", "payment.recorded": "Paiement encaissé",
  "payment.outside_cash_session": "Paiement hors session de caisse",
  "booking.conflict_override": "Conflit forcé",
  "booking.commission_set": "Commission définie",
};

const CHANGE_LABELS = {
  formula: "formule", customer: "client", vehicles: "véhicules", employees: "guides",
  participants_count: "participants", booking_date: "date", start_time: "horaire", source: "source",
};

function fmtMoneyMaybe(v, currency) {
  return typeof v === "number" ? formatMoney(v, currency) : v;
}

function BookingHistory({ bookingId, currency }) {
  const { orgId } = useAuth();
  const { data } = useQuery({
    queryKey: ["booking-audit", bookingId, orgId],
    queryFn: async () => (await api.get(`/api/bookings/${bookingId}/audit`, { params: { organization_id: orgId } })).data,
    enabled: !!bookingId,
  });
  const events = data?.events || [];
  return (
    <Block title="Historique">
      <div className="space-y-3 border-l-2 border-slate-100 pl-3" data-testid="booking-history">
        {events.length === 0 ? <p className="text-sm text-slate-400">Aucun évènement</p> : events.map((e, i) => {
          const nv = e.new_value || {};
          let detail = "";
          if (e.action === "payment.recorded") detail = `${fmtMoneyMaybe(nv.amount_cents, currency)} · ${PAYMENT_METHODS[nv.method] || nv.method || ""}`;
          else if (e.action === "booking.status_changed") detail = nv.status || "";
          else if (e.action === "booking.price_override") detail = `${fmtMoneyMaybe(nv.calculated, currency)} → ${fmtMoneyMaybe(nv.total, currency)}`;
          else if (e.action === "booking.repriced") detail = `Total ${fmtMoneyMaybe(nv.total, currency)}${nv.discount ? ` · remise ${fmtMoneyMaybe(nv.discount, currency)}` : ""}`;
          else if (e.action === "booking.cancelled") detail = nv.reason || "";
          else if (e.action === "booking.conflict_override") detail = (nv.conflicts || []).join(", ");
          else if (e.action === "booking.updated") detail = (nv.changes || []).map((c) => CHANGE_LABELS[c] || c).join(", ");
          else if (e.action === "booking.created") detail = nv.reference || "";
          return (
            <div key={i} className="relative" data-testid={`history-event-${i}`}>
              <span className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full bg-primary" />
              <p className="text-sm font-medium text-slate-800">{ACTION_LABELS[e.action] || e.action}{detail ? <span className="font-normal text-slate-500"> · {detail}</span> : ""}</p>
              <p className="text-xs text-slate-400">{(e.created_at || "").slice(0, 16).replace("T", " ")}{e.actor_name ? ` · par ${e.actor_name}` : ""}</p>
            </div>
          );
        })}
      </div>
    </Block>
  );
}
